import React, { useEffect, useState } from "react";
import {
  LoadingPageWrapper,
  StandardPageWrapper,
} from "../../../components/layout/StandardPageWrapper";
import { useOrganizationVendors, usePrimaryOrganization } from "../../../hooks";
import {
  GDPRProcessorRecommendation,
  GdprSccVendorReviewStatus,
  GppSignalStatus,
  CollectionContext,
  OrganizationDataRecipientDto,
  ProcessingRegion,
} from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import _ from "lodash";
import clsx from "clsx";
import { ArrowRightCircleIcon } from "@heroicons/react/24/outline";
import { OrganizationDto } from "../../../../common/service/server/dto/OrganizationDto";
import {
  Button,
  Breadcrumbs,
  ExplanatoryCard,
  Modal,
  SimpleCheckbox,
  Slideout,
  TextInput,
  ToolTip,
} from "@truevault/aquilo";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowsUpDownIcon,
  ArrowTopRightOnSquareIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import { UpdateVendorArgs } from "../components/workflows/create-data-recipient/ClassificationForDataRecipientPage";
import { Api } from "../../../../common/service/Api";
import { useActionRequest } from "../../../../common/hooks/api";
import { Form } from "react-final-form";
import { VendorRadioSection } from "../../../components/get-compliant/vendors/VendorFlagWithDocumentation";
import { isStorageLocation, isThirdPartyRecipient } from "../../../util/vendors";
import { SurveyAnswers, useSurvey } from "../../../../common/hooks/api/useSurvey";
import { SELLING_AND_SHARING_SURVEY_NAME } from "../../get-compliant/data-recipients/shared";
import {
  calculateDisclosureMeta,
  ClassificationType,
  initialStateFrom,
  isGDPRControllerDocumentationOptional,
  isThirdPartyDocumentationOptional,
} from "../../../components/organisms/data-recipients/DataRecipientDisclosureSettings";
import {
  saveCaContractRequirementDocumentation,
  saveGdprDocumentation,
  saveGppSignalDocumentation,
  saveSccDocumentation,
  saveServiceProviderDocumentation,
} from "./saveFiles";
import {
  caContractOptionsForVendor,
  gdprClassificationHelpText,
  gdprClassificationOptionsForVendor,
  getClassificationIdForClassificationType,
  getVendorClassificationSlug,
  gppSignalHelpText,
  internationalDataTransferOptionsForVendor,
  isVendorServiceClassificationLockedToThirdParty,
  usClassificationHelpText,
  usClassificationOptionsForVendor,
} from "./vendorDocumentationUtils";
import { VendorBooleanQuestion } from "../../../components/vendor/VendorBooleanQuestion";
import { RouteHelpers, Routes } from "../../../root/routes";
import {
  getDocumentationStatusCell,
  getGdprClassificationCell,
  getVendorClassificationCell,
  VendorTableCellPillDisplay,
} from "./vendorTableCellDisplay";
import { ProcessingRegionToFlagIcon, VendorRegionCheckbox } from "./vendorDocumentationEditorUi";
import { ContactVendorsModal } from "./ContactVendorsModal";
import {
  clearVendorDocumentationContactSuccess,
  persistVendorDocumentationContactSuccess,
  readVendorDocumentationContactSuccess,
} from "./vendorDocumentationContactSuccessStorage";
import { EmailVendorDialog } from "./emailVendorDialog";
import validator from "validator";
import { CircularProgress } from "@mui/material";
import { ProgressFooter, ProgressFooterActions } from "../../get-compliant/ProgressFooter";
import { useHistory } from "react-router";
import { Iso8601Date } from "../../../../common/service/server/Types";
import { CheckCircleIcon, EnvelopeIcon } from "@heroicons/react/24/solid";
import { Fmt } from "../../../formatters";
import { SafeCountriesDialog } from "../../../components/dialog/SafeCountriesDialog";
import { PrivacyEmailSetupCard } from "../../../components/privacyCenter/PrivacyEmailSetupCard";
import {
  canSendPrivacyEmail,
  PRIVACY_EMAIL_SETUP_REQUIRED_TOOLTIP,
  shouldShowPrivacyEmailSetupCard,
} from "../../../util/privacyEmailSetup";
import { Disableable } from "../../../components/Disableable";
import { VENDOR_CATEGORY } from "../../../types/Vendor";
import { isEmployeeOnlyCollectionContext } from "../vendor-inventory/vendorInventoryUtils";
import { DEVELOPER_INSTRUCTIONS_SURVEY_NAME } from "../../stay-compliant/PrivacyNoticeChecklistItems";
import {
  useInvalidateOnboardingProgress,
  useOnboardingProgress,
} from "../../../../common/hooks/useOnboardingProgress";

// Mirrors SurveySlugs.SKIPPED_VENDOR_DOCUMENTATION_ONBOARDING: "Skip for now" lets onboarding move on to
// Data Retention while the docs stay incomplete. Stored with the org, so it holds across browsers.
const SKIPPED_VENDOR_DOCUMENTATION_ONBOARDING_SLUG = "skipped-vendor-documentation-onboarding";

export const shouldShowContractFulfillmentSuggestionIcon = (
  vendor: OrganizationDataRecipientDto,
): boolean => {
  // POL-6737: Payment Processor and Shipping Service vendors should not show this suggestion.
  return (
    vendor.isPotentiallyExceptedFromSccs &&
    ![VENDOR_CATEGORY.PAYMENT_PROCESSOR, VENDOR_CATEGORY.SHIPPING_SERVICE].includes(vendor.category)
  );
};

export const doesVendorHaveUsClassification = (vendor: OrganizationDataRecipientDto): boolean => {
  const classificationSlug = getVendorClassificationSlug(vendor);

  if (!classificationSlug || classificationSlug === "needs-review") {
    return false;
  }

  const isThirdParty =
    classificationSlug === "third-party" || isVendorServiceClassificationLockedToThirdParty(vendor);
  // If third party, documentation is optional
  if (isThirdParty) {
    return true;
  }

  return Boolean(
    // check for our documentation URL from vendor database
    vendor.serviceProviderLanguageUrl ||
      // check for user-provided documentation URL
      vendor.publicTosUrl ||
      // check for user-provided documentation file upload
      (vendor.tosFileName && vendor.tosFileName !== "DELETE_") ||
      // no external documentation required if contract is stored internally
      vendor.contractStoredInternally === true,
  );
};

export const isUsClassificationRequiredForVendor = (
  vendor: OrganizationDataRecipientDto,
  org: OrganizationDto,
  collectionContext: CollectionContext | CollectionContext[] = "CONSUMER",
): boolean => {
  if (isEmployeeOnlyCollectionContext(vendor.collectionContext ?? [])) {
    return false;
  }

  // Must not be a storage location
  if (isStorageLocation(vendor)) {
    return false;
  }

  // Must be in US or Canada
  let isUsRegion = vendor.processingRegions.includes("UNITED_STATES");
  let isCanadaRegion = org.featurePipeda && vendor.processingRegions.includes("CANADA");
  if (!isUsRegion && !isCanadaRegion) {
    return false;
  }

  // Must match the configured page context
  const requiredContexts = Array.isArray(collectionContext)
    ? collectionContext
    : [collectionContext];
  let isInContext = vendor?.collectionContext?.some((context) =>
    requiredContexts.includes(context),
  );
  if (!isInContext) {
    return false;
  }

  // If in US, then must not be regulated by other US privacy laws
  if (isUsRegion && vendor.regulatedByOtherUSPrivacyLaws === true) {
    return false;
  }

  return true;
};

// May still return true or false even if `vendor` is not in the EEA/UK region, so caller is responsible for checking GDPR status for both org and vendor
export const isVendorGdprClassificationComplete = (
  vendor: OrganizationDataRecipientDto,
): boolean => {
  if (vendor.gdprProcessorSetting === "Processor") {
    return Boolean(
      vendor.gdprProcessorGuaranteeUrl ||
        vendor.gdprProcessorGuaranteeFileName ||
        vendor.gdprProcessorLanguageUrl ||
        vendor.gdprContractStoredInternally === true,
    );
  } else if (vendor.gdprProcessorSetting === "Controller") {
    return Boolean(
      vendor.gdprControllerGuaranteeUrl ||
        vendor.gdprControllerGuaranteeFileName ||
        vendor.gdprProcessorLanguageUrl ||
        vendor.gdprContractStoredInternally === true,
    );
  }

  return false;
};

export const isGdprInternationalDataTransferComplete = (
  vendor: OrganizationDataRecipientDto,
): boolean => {
  if (vendor.gdprSccVendorReviewStatus === "DOCS_PROVIDED") {
    return Boolean(vendor.gdprSccFileName || vendor.gdprSccUrl);
  }

  return Boolean(vendor.gdprSccVendorReviewStatus || vendor.gdprHasSccRecommendation);
};

export const doesGdprInternationalDataTransferApplyForVendor = (
  vendor: OrganizationDataRecipientDto,
): boolean => {
  // Use `!== false` instead of `== true` since if the field is empty, we want to default to enabling international data transfers for the vendor
  return vendor.gdprInternationalDataTransferRulesApply !== false;
};

// Note: when updating this, also update `checkConsentManagementDevInstructionsCompletionStatus` in `OrganizationOnboardingProgressService.kt`
export const doesGppSignalApplyForVendor = (vendor: OrganizationDataRecipientDto): boolean => {
  if (isEmployeeOnlyCollectionContext(vendor.collectionContext ?? [])) {
    return false;
  }

  // GPP signal doesn't apply if vendor is in the US and is regulated by other US privacy laws (POL-6562)
  if (
    vendor.processingRegions.includes("UNITED_STATES") &&
    vendor.regulatedByOtherUSPrivacyLaws === true
  ) {
    return false;
  }

  return (
    getVendorClassificationSlug(vendor) === "third-party" &&
    (vendor?.ccpaIsSelling || vendor?.isAlwaysDataSellingVendor || vendor?.ccpaIsSharing)
  );
};

export const isVendorGppSignalDocumentationComplete = (
  vendor: OrganizationDataRecipientDto,
): boolean => {
  if (!vendor.gppSignalStatus) {
    return false;
  }

  // Only require documentation for the GPP_SIGNAL_RESPECTED answer
  if (vendor.gppSignalStatus !== "GPP_SIGNAL_RESPECTED") {
    return true;
  }

  // Since signal is respected, we need to check if the signal document is uploaded
  return Boolean(
    vendor.gppSignalDocumentationUrl ||
      (vendor.gppSignalDocumentationFileName &&
        vendor.gppSignalDocumentationFileName !== "DELETE_"),
  );
};

export const isCaContractFieldRequiredForVendor = (
  vendor: OrganizationDataRecipientDto,
): boolean => {
  if (isEmployeeOnlyCollectionContext(vendor.collectionContext ?? [])) {
    return false;
  }

  return (
    vendor.processingRegions.includes("UNITED_STATES") &&
    vendor.regulatedByOtherUSPrivacyLaws !== true
  );
};

export const isVendorCaContractDocumentationComplete = (
  vendor: OrganizationDataRecipientDto,
): boolean => {
  return Boolean(
    vendor.caContractRequirementUrl ||
      vendor.cpraContractLanguageUrl ||
      (vendor.caContractRequirementFileName && vendor.caContractRequirementFileName !== "DELETE_"),
  );
};

// Determine whether the vendor appears in the Unclassified Vendors or Classified Vendors table
// To be considered classified, a vendor must have a US Classification and a CA Contract requirement documentation
export const isVendorDocumentationClassified = (
  org: OrganizationDto,
  vendor: OrganizationDataRecipientDto,
  collectionContext: CollectionContext | CollectionContext[] = "CONSUMER",
  includeEmptyContext = true,
) => {
  const requiredContexts = Array.isArray(collectionContext)
    ? collectionContext
    : [collectionContext];
  const vendorCollectionContexts = vendor?.collectionContext ?? [];
  const isInContext =
    (includeEmptyContext && vendorCollectionContexts.length === 0) ||
    vendorCollectionContexts.some((context) => requiredContexts.includes(context));
  if (!isInContext) {
    return true;
  }

  if (
    isUsClassificationRequiredForVendor(vendor, org, collectionContext) &&
    !doesVendorHaveUsClassification(vendor)
  ) {
    return false;
  }

  if (
    isCaContractFieldRequiredForVendor(vendor) &&
    !isVendorCaContractDocumentationComplete(vendor)
  ) {
    return false;
  }

  if (doesGppSignalApplyForVendor(vendor) && !isVendorGppSignalDocumentationComplete(vendor)) {
    return false;
  }

  // EEA/UK region checks
  const isEeaUk =
    !isEmployeeOnlyCollectionContext(vendor.collectionContext ?? []) &&
    org?.featureGdpr &&
    vendor.processingRegions.includes("EEA_UK");
  if (isEeaUk) {
    if (!isVendorGdprClassificationComplete(vendor)) {
      return false;
    }
    if (
      doesGdprInternationalDataTransferApplyForVendor(vendor) &&
      !isGdprInternationalDataTransferComplete(vendor)
    ) {
      return false;
    }
  }

  return true;
};

export type VendorDocumentationPageConfig = {
  collectionContext: CollectionContext[];
  includeEmptyContext: boolean;
  breadcrumbTitle: string;
  breadcrumbSource: string;
  breadcrumbSourceRoute: string;
  doneRoute: string;
  /** The step at doneRoute, as the skip prompt names it. */
  doneStepName: string;
  headerElement?: React.ReactNode;
};

const CONSUMER_VENDOR_DOCUMENTATION_CONFIG: VendorDocumentationPageConfig = {
  collectionContext: ["CONSUMER"],
  includeEmptyContext: true,
  breadcrumbTitle: "Vendor Documents",
  breadcrumbSource: "Data Map",
  breadcrumbSourceRoute: Routes.dataMap.dashboard,
  doneRoute: Routes.dataMap.dataRetention,
  doneStepName: "Data Retention",
};

// A vendor is considered "recently contacted" if we sent it a documentation/classification
// request within the last two months and are presumably awaiting a reply.
export const VENDOR_RECENTLY_CONTACTED_MS = 60 * 24 * 60 * 60 * 1000;
export const isVendorRecentlyContacted = (vendor: OrganizationDataRecipientDto): boolean =>
  Boolean(
    vendor.serviceProviderInfoRequestSentDate &&
      new Date(vendor.serviceProviderInfoRequestSentDate).getTime() >=
        Date.now() - VENDOR_RECENTLY_CONTACTED_MS,
  );

export const hasVendorEverBeenContacted = (vendor: OrganizationDataRecipientDto) => {
  // Note that even though we reset the sent date to "1970-01-01T00:00:00Z" to indicate the vendor should be contacted again, having a non-empty value means that the vendor was contacted at least once at some point in the past
  return vendor.serviceProviderInfoRequestSentDate?.length > 0;
};

export function VendorDocumentationPage() {
  return <VendorDocumentationPageContent config={CONSUMER_VENDOR_DOCUMENTATION_CONFIG} />;
}

export function VendorDocumentationPageContent({
  config,
}: {
  config: VendorDocumentationPageConfig;
}) {
  const history = useHistory();

  const [org, ,] = usePrimaryOrganization();
  const [allVendors, , refreshVendors] = useOrganizationVendors(
    org?.id,
    /* excludeRecipientCategories = */ [],
    /* includeDataStorageLocations = */ false,
    /* excludeIncomplete = */ undefined,
    /* context = */ config.collectionContext,
    /* onlyUseSettled = */ true,
    /* includeEmptyContext = */ config.includeEmptyContext,
  );

  const [vendorsState, setVendorsState] = useState<OrganizationDataRecipientDto[] | null>(null);

  const [sidebarOpenForVendorId, setSidebarOpenForVendorId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [contactVendorsModalOpen, setContactVendorsModalOpen] = useState(false);
  const [donePromptModalOpen, setDonePromptModalOpen] = useState(false);
  const [isSkipping, setIsSkipping] = useState(false);
  const canSendEmail = canSendPrivacyEmail(org);
  const showPrivacyEmailSetupCard = shouldShowPrivacyEmailSetupCard(org);
  const isConsumerConfig = config.collectionContext.includes("CONSUMER");
  const { setAnswer: setDevInstructionsAnswer } = useSurvey(
    org?.id,
    DEVELOPER_INSTRUCTIONS_SURVEY_NAME,
  );
  const [onboardingProgress] = useOnboardingProgress(org?.id);
  const invalidateOnboardingProgress = useInvalidateOnboardingProgress(org?.id);

  // Unverified vendors nobody has asked for documentation yet. The backend already counts a
  // contacted vendor as done, so once this is empty the step is finished.
  const vendorsAwaitingDocumentation = (vendorsState || []).filter(
    (v) =>
      !isVendorDocumentationClassified(
        org,
        v,
        config.collectionContext,
        config.includeEmptyContext,
      ) && !hasVendorEverBeenContacted(v),
  );
  const isDocumentationStepDone = vendorsAwaitingDocumentation.length === 0;

  const goToNextStep = () => {
    invalidateOnboardingProgress();
    history.push(config.doneRoute);
  };

  const handleDoneClick = () => {
    if (isDocumentationStepDone) {
      goToNextStep();
    } else {
      setDonePromptModalOpen(true);
    }
  };

  const skipForNow = async () => {
    setIsSkipping(true);
    try {
      if (isConsumerConfig) {
        await setDevInstructionsAnswer(SKIPPED_VENDOR_DOCUMENTATION_ONBOARDING_SLUG, "true");
      }
      setDonePromptModalOpen(false);
      goToNextStep();
    } catch {
      // The survey mutation shows its own error snackbar; stay put so the user can retry.
    } finally {
      setIsSkipping(false);
    }
  };

  const [contactVendorsSuccess, setContactVendorsSuccessState] = useState<{
    date: string;
    names: string[];
    vendorIds: string[];
  } | null>(null);

  useEffect(() => {
    if (!org?.id) return;
    const parsed = readVendorDocumentationContactSuccess(org.id);
    if (parsed) {
      setContactVendorsSuccessState(parsed);
    }
  }, [org?.id]);

  const setContactVendorsSuccess = (
    value: { date: string; names: string[]; vendorIds: string[] } | null,
  ) => {
    setContactVendorsSuccessState(value);
    if (org?.id) {
      if (value) {
        persistVendorDocumentationContactSuccess(org.id, value);
      } else {
        clearVendorDocumentationContactSuccess(org.id);
      }
    }
  };

  const { answers: sellingAndSharingAnswers } = useSurvey(org?.id, SELLING_AND_SHARING_SURVEY_NAME);

  // Keep track of the completion state on initial load so we know whether to show the footer with a Done button
  const [isInitiallyCompleted, setIsInitiallyCompleted] = useState(null);

  useEffect(() => {
    if (allVendors && vendorsState === null) {
      setVendorsState(allVendors);

      if (isInitiallyCompleted === null) {
        setIsInitiallyCompleted(
          allVendors.every((v) =>
            isVendorDocumentationClassified(
              org,
              v,
              config.collectionContext,
              config.includeEmptyContext,
            ),
          ),
        );
      }
    }
  }, [
    allVendors,
    vendorsState,
    setVendorsState,
    isInitiallyCompleted,
    setIsInitiallyCompleted,
    config.collectionContext,
    config.includeEmptyContext,
  ]);

  const { fetch: updateDataRecipient } = useActionRequest({
    api: async (v: UpdateVendorArgs) => {
      const { vendorId, vendorSettings } = v;
      await Api.organizationVendor.addOrUpdateVendor(org?.id, vendorId, vendorSettings);
    },
    onSuccess: refreshVendors,
  });

  if (vendorsState === null) {
    return <LoadingPageWrapper />;
  }

  // The next step (config.doneRoute) is each data map's retention policy.
  const isBeforeDataRetention =
    onboardingProgress != null &&
    (isConsumerConfig
      ? org?.finishedOnboardingLatch === false &&
        !onboardingProgress.dataMapOnboardingProgress.isDataRetentionComplete
      : !onboardingProgress.hrCenterOnboardingProgress.isFinishedWithHrRetentionPolicy);

  const visibleVendors = (vendorsState || []).filter((v) =>
    v.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const [classifiedVendors, unclassifiedVendors] = _.partition(
    visibleVendors,
    (e) =>
      isVendorDocumentationClassified(org, e, config.collectionContext, config.includeEmptyContext), // returning true from predication lambda will place result in first list (which is classified vendors)
  );

  // The "Contact Unverified Vendors" action is intentionally independent of the table search:
  // it always targets every unverified vendor (across the full list), even ones hidden by the
  // current search filter. Only the rendered table rows respect the search.
  const allUnclassifiedVendors = (vendorsState || []).filter(
    (v) =>
      !isVendorDocumentationClassified(
        org,
        v,
        config.collectionContext,
        config.includeEmptyContext,
      ),
  );
  const allVendorsVerified = allUnclassifiedVendors.length === 0;
  const contactUnverifiedDisabled = allVendorsVerified || !canSendEmail;
  const contactUnverifiedTooltip = allVendorsVerified
    ? "All vendors are verified"
    : !canSendEmail
    ? PRIVACY_EMAIL_SETUP_REQUIRED_TOOLTIP
    : null;

  const updateVendor = async (updateVendorArgs: UpdateVendorArgs) => {
    if (contactVendorsSuccess?.vendorIds?.includes(updateVendorArgs.vendorId)) {
      setContactVendorsSuccess(null);
    }
    setVendorsState(
      vendorsState.map((vendor) => {
        if (vendor.vendorId === updateVendorArgs.vendorId) {
          let updatedVendor = { ...vendor, ...updateVendorArgs.vendorSettings };
          // Re-create the classification DTO object for the local state (normally this is done by the API, so to be consistent we apply the same mutation to our local state)
          let newClassification = updatedVendor.classificationOptions.find(
            (s) => s.id === updatedVendor.classificationId,
          );
          if (newClassification) {
            updatedVendor.classification = newClassification;
          }
          return updatedVendor;
        }
        return vendor;
      }),
    );

    // Ensure we convert the classification DTO object to the classification ID (if a new classification ID wasn't provided)
    if (
      !updateVendorArgs?.vendorSettings?.classificationId &&
      "classification" in updateVendorArgs.vendorSettings
    ) {
      updateVendorArgs.vendorSettings.classificationId =
        // @ts-ignore - we know that if the classification property exists, it's a DTO object and must have an ID
        updateVendorArgs.vendorSettings.classification?.id;
    }
    await updateDataRecipient(updateVendorArgs);
  };

  return (
    <StandardPageWrapper
      fullWidth
      footer={
        // Hide the footer if the sidebar is open. A list that was already verified on load only
        // needs it while onboarding still has Data Retention ahead.
        sidebarOpenForVendorId || (isInitiallyCompleted && !isBeforeDataRetention) ? undefined : (
          <ProgressFooter
            actions={
              <ProgressFooterActions
                nextContent={isDocumentationStepDone ? "Done with Vendor Docs" : "Skip for now"}
                nextOnClick={handleDoneClick}
                nextDisabled={false}
              />
            }
          />
        )
      }
    >
      <div className="tw-boundary">
        <div className="mb-[24px]">
          <Breadcrumbs
            hideHomeIcon={true}
            title={config.breadcrumbTitle}
            source={config.breadcrumbSource}
            sourceRoute={config.breadcrumbSourceRoute}
          />
        </div>

        {config.headerElement}

        {showPrivacyEmailSetupCard && org && (
          <PrivacyEmailSetupCard
            className="max-w-[1055px]"
            onSetupClick={() => history.push(RouteHelpers.organization.domain(org.id))}
          />
        )}

        <div className="max-w-[1055px] mb-[24px]">
          <ExplanatoryCard
            variant="hairline"
            topBody={
              <div>
                <h3 className="text-lg font-medium leading-6 text-neutral-800 mb-3">
                  Vendor Documentation Requirements
                </h3>
                <p className="text-[15px] leading-[21px] text-neutral-600 mb-[24px]">
                  The table below lists all the vendors in your Data Map. Each column is a privacy
                  requirement you fulfill by uploading or linking to the vendor's documentation
                  (found in their Privacy Policy, Terms of Service, Data Processing Agreements, or
                  other contracts).
                </p>
                <div className="flex flex-col gap-[8px] pl-[12px]">
                  {[
                    {
                      title: "Review what's already filled in",
                      body: "TrueVault has automatically filled in parts of the table for any vendors in our database with publicly available documentation.",
                    },
                    {
                      title: "Add documentation for unverified requirements",
                      body: (
                        <>
                          For anything marked{" "}
                          <BadgedTextPill
                            cellPillDisplay={{ color: "yellow", text: "Unverified" }}
                          />
                          , click the arrow on the far right of a vendor's row to open the side
                          panel, then upload or link to the document. For more information on each
                          requirement, refer to our{" "}
                          <a
                            href="https://knowledge.truevault.com/article/351-privacy-documentation-for-vendors"
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-700 underline font-medium"
                          >
                            Vendor Documentation Guide
                          </a>{" "}
                          or hover over the info icons next to the table headers.
                        </>
                      ),
                    },
                    {
                      title: "Contact the vendor if you can't find its missing documentation",
                      body: "If the documentation isn't publicly available, enter the vendor's email in the side panel and send one of the pre-written templates to request it.",
                    },
                    {
                      title: "Update the table as replies come in",
                      body: "Replies come directly to your privacy email. Come back here and update the table as you receive them.",
                    },
                  ].map((step, index) => (
                    <div className="flex" key={index}>
                      <div className="rounded-[100px] text-[14px] text-neutral-800 bg-neutral-200 w-[21px] h-[21px] flex-shrink-0 flex flex-col justify-center items-center mt-[3px]">
                        {index + 1}
                      </div>
                      <div className="ml-[12px]">
                        <div className="text-[16px] font-medium leading-[20px] text-neutral-900">
                          {step.title}
                        </div>
                        <div className="text-[14px] leading-[21px] text-neutral-600">
                          {step.body}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="flex">
                    <div className="rounded-[100px] text-[14px] text-neutral-800 bg-neutral-200 w-[21px] h-[21px] flex-shrink-0 flex flex-col justify-center items-center mt-[3px]">
                      5
                    </div>
                    <div className="ml-[12px]">
                      <div className="text-[16px] font-medium leading-[20px] text-neutral-900">
                        Missing documentation for multiple vendors?
                      </div>
                      <div className="text-[14px] leading-[21px] text-neutral-600">
                        Reach out to every vendor with unverified documentation at once.
                      </div>
                      <div className="mt-[4px]">
                        <Disableable
                          disabled={contactUnverifiedDisabled}
                          disabledTooltip={contactUnverifiedTooltip}
                          isBlock={true}
                        >
                          <span className="inline-flex">
                            <Button
                              color="primary"
                              size="s"
                              disabled={contactUnverifiedDisabled}
                              onClick={() => setContactVendorsModalOpen(true)}
                            >
                              Contact Unverified Vendors
                            </Button>
                          </span>
                        </Disableable>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            }
            expandable={false}
          />
        </div>

        {contactVendorsSuccess && (
          <div className="mb-[24px] max-w-[1055px] p-[16px] rounded-[6px] bg-green-100">
            <div className="text-green-800 text-[14px] font-medium">
              The vendors listed below were contacted successfully on{" "}
              {new Date(contactVendorsSuccess.date).toLocaleDateString(undefined, {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
              .
            </div>
            <ul className="mt-2 list-disc pl-4 text-sm text-green-700">
              {contactVendorsSuccess.names.map((name) => (
                <li key={name}>{name}</li>
              ))}
            </ul>
          </div>
        )}

        {donePromptModalOpen && (
          <Modal
            title={`${vendorsAwaitingDocumentation.length} ${
              vendorsAwaitingDocumentation.length === 1 ? "vendor is" : "vendors are"
            } still missing documentation`}
            body={
              canSendEmail
                ? `Email them for their documentation now and we'll remind you to check their replies, or skip this for now and move on to ${config.doneStepName}. You can come back to it anytime.`
                : `Set up your privacy email to request their documentation, or skip this for now and move on to ${config.doneStepName}. You can come back to it anytime.`
            }
            firstButton={isSkipping ? "Skipping…" : "Skip for now"}
            secondButton={canSendEmail ? "Contact vendors" : "Set up privacy email"}
            firstButtonColor="tertiary"
            secondButtonColor="primary"
            firstButtonDisabled={isSkipping}
            // A pending skip navigates when it lands, so the prompt can't be left mid-skip.
            secondButtonDisabled={isSkipping}
            alignText="left"
            alignButton="right"
            showOverlay={true}
            closeOnOverlayClick={false}
            onFirstButtonClick={skipForNow}
            onSecondButtonClick={() => {
              setDonePromptModalOpen(false);
              if (canSendEmail) {
                setContactVendorsModalOpen(true);
              } else {
                history.push(RouteHelpers.organization.domain(org.id));
              }
            }}
            onClose={() => {
              if (!isSkipping) {
                setDonePromptModalOpen(false);
              }
            }}
          />
        )}

        {contactVendorsModalOpen && (
          <ContactVendorsModal
            unclassifiedVendors={allUnclassifiedVendors}
            org={org}
            collectionContext={config.collectionContext}
            onClose={() => setContactVendorsModalOpen(false)}
            onSuccess={(contactedNames, contactedVendorIds) => {
              const contactDate = new Date().toISOString();
              setContactVendorsSuccess({
                date: contactDate,
                names: contactedNames,
                vendorIds: contactedVendorIds,
              });
              setVendorsState((prev) =>
                (prev ?? []).map((v) =>
                  contactedVendorIds.includes(v.vendorId)
                    ? { ...v, serviceProviderInfoRequestSentDate: contactDate }
                    : v,
                ),
              );
              refreshVendors();
            }}
          />
        )}

        <div className="max-w-[1055px] mb-[24px] flex items-center gap-[12px]">
          <div className="w-[300px]">
            <div className="relative rounded-md shadow-sm">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </div>
              <input
                type="text"
                className="block w-full rounded-md border-0 py-1.5 pl-10 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                placeholder="Search Table"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
          <div className="rounded-full bg-blue-100 text-blue-800 px-[12px] py-[4px] font-medium text-[14px] whitespace-nowrap">
            {visibleVendors.length} {visibleVendors.length === 1 ? "vendor" : "vendors"}
          </div>
        </div>

        {classifiedVendors.length > 0 || unclassifiedVendors.length > 0 ? (
          <VendorDocumentationTable
            org={org}
            classifiedVendors={classifiedVendors}
            unclassifiedVendors={unclassifiedVendors}
            updateVendor={updateVendor}
            sidebarOpenForVendorId={sidebarOpenForVendorId}
            setSidebarOpenForVendorId={setSidebarOpenForVendorId}
            sellingAndSharingAnswers={sellingAndSharingAnswers}
            allVendors={vendorsState}
            collectionContext={config.collectionContext}
            includeEmptyContext={config.includeEmptyContext}
          />
        ) : (
          <div className="text-neutral-800 text-[14px] italic">
            {searchQuery ? "No vendors match your search" : "No vendors"}
          </div>
        )}
      </div>
    </StandardPageWrapper>
  );
}

type VendorTableSortOrder =
  | "name_ascending"
  | "name_descending"
  | "us_classification_ascending"
  | "us_classification_descending"
  | "gdpr_classification_ascending"
  | "gdpr_classification_descending"
  | "documentation_status_ascending"
  | "documentation_status_descending";

function VendorDocumentationTable({
  org,
  classifiedVendors,
  unclassifiedVendors,
  updateVendor,
  sidebarOpenForVendorId,
  setSidebarOpenForVendorId,
  sellingAndSharingAnswers,
  allVendors,
  collectionContext,
  includeEmptyContext,
}: {
  org: OrganizationDto;
  classifiedVendors: OrganizationDataRecipientDto[];
  unclassifiedVendors: OrganizationDataRecipientDto[];
  updateVendor: (v: UpdateVendorArgs) => Promise<void>;
  sidebarOpenForVendorId: string | null;
  setSidebarOpenForVendorId: (id: string | null) => void;
  sellingAndSharingAnswers: SurveyAnswers;
  allVendors: OrganizationDataRecipientDto[];
  collectionContext: CollectionContext[];
  includeEmptyContext: boolean;
}) {
  const [sortOrder, setSortOrder] = useState<VendorTableSortOrder | null>("name_ascending");

  // Same header chrome as the Vendor List tables: white row, hairline divider, small caps labels.
  const tableHeaderStyles = clsx(
    "py-[11px]",
    "bg-neutral-0",
    "border-b border-solid border-neutral-200",
    "text-[12px] font-medium text-neutral-600 uppercase tracking-[0.4px]",
  );
  const tableHeader = (
    name: string,
    className: string = "",
    tooltipText: string = "",
    onSortClick?: () => void,
  ) => {
    let hasTooltip = tooltipText && tooltipText.length > 0;
    let hasSortIcon = Boolean(onSortClick);

    if (!hasTooltip && !hasSortIcon) {
      return (
        <th scope="col" className={tableHeaderStyles + " " + className}>
          {name}
        </th>
      );
    }

    return (
      <th scope="col" className={tableHeaderStyles + " " + className}>
        <div
          className={clsx(
            "flex items-center gap-[8px]",
            className.includes("text-left") ? "justify-start" : "justify-center",
          )}
        >
          <span>{name}</span>
          {hasSortIcon && (
            <ArrowsUpDownIcon
              className="w-[16px] h-[16px] text-neutral-800 cursor-pointer"
              onClick={onSortClick}
            />
          )}
          {hasTooltip && (
            <ToolTip text={tooltipText} width="210px">
              <InformationCircleIcon className="w-[16px] h-[16px] text-gray-500" />
            </ToolTip>
          )}
        </div>
      </th>
    );
  };

  // Split the unverified vendors into those we've recently contacted (awaiting reply) and the rest
  const [recentlyContactedVendors, unverifiedVendors] = _.partition(
    unclassifiedVendors,
    isVendorRecentlyContacted,
  );

  let unverifiedVendorsSorted = unverifiedVendors,
    recentlyContactedVendorsSorted = recentlyContactedVendors,
    classifiedVendorsSorted = classifiedVendors;
  if (sortOrder) {
    const sortFn = (a: OrganizationDataRecipientDto, b: OrganizationDataRecipientDto) =>
      vendorComparisonFn(a, b, sortOrder, org, collectionContext);
    unverifiedVendorsSorted = unverifiedVendors.sort(sortFn);
    recentlyContactedVendorsSorted = recentlyContactedVendors.sort(sortFn);
    classifiedVendorsSorted = classifiedVendors.sort(sortFn);
  }

  const sectionHeaderRow = (label: string, colorClassName: string) => (
    <tr className="border-gray-200">
      <th
        scope="colgroup"
        className={clsx("font-medium text-left text-[14px] py-[11px] pl-[24px]", colorClassName)}
        colSpan={org.featureGdpr ? 6 : 5}
      >
        {label}
      </th>
    </tr>
  );

  // Previous/Next navigation should follow the vendor's placement in the table as displayed
  // (Unverified → Recently Contacted → Verified, each in its sorted order), not a single
  // alphabetical list across the whole table.
  const tableOrderedVendors = [
    ...unverifiedVendorsSorted,
    ...recentlyContactedVendorsSorted,
    ...classifiedVendorsSorted,
  ];

  const renderVendorRows = (vendors: OrganizationDataRecipientDto[]) =>
    vendors.map((vendor) => (
      <VendorDocumentationTableRow
        key={vendor.id}
        org={org}
        vendor={vendor}
        updateVendor={updateVendor}
        isSidebarOpen={sidebarOpenForVendorId === vendor.vendorId}
        openSidebar={() => setSidebarOpenForVendorId(vendor.vendorId)}
        closeSidebar={() => setSidebarOpenForVendorId(null)}
        sellingAndSharingAnswers={sellingAndSharingAnswers}
        allVendors={allVendors}
        collectionContext={collectionContext}
        includeEmptyContext={includeEmptyContext}
        navigateNextVendorInSidebar={() => {
          let idx = tableOrderedVendors.findIndex((v) => v.id === vendor.id);
          if (idx == -1) return;
          let nextIdx = (idx + 1) % tableOrderedVendors.length;
          setSidebarOpenForVendorId(tableOrderedVendors[nextIdx].vendorId);
        }}
        navigatePreviousVendorInSidebar={() => {
          let idx = tableOrderedVendors.findIndex((v) => v.id === vendor.id);
          if (idx == -1) return;
          let prevIdx = (idx - 1 + tableOrderedVendors.length) % tableOrderedVendors.length;
          setSidebarOpenForVendorId(tableOrderedVendors[prevIdx].vendorId);
        }}
      />
    ));

  return (
    <div
      className={clsx(
        "border border-solid border-neutral-200 rounded-xl overflow-x-hidden shadow-[0px_1px_2px_0px_#1018280D] bg-white",
        "max-w-[1055px]",
      )}
      style={{ overflow: "clip" }}
    >
      <table className="w-full" style={{ tableLayout: "fixed" }}>
        {org.featureGdpr ? (
          <colgroup>
            <col style={{ width: "360px" }} /> {/* Vendor Name */}
            <col style={{ width: "120px" }} /> {/* Region */}
            <col style={{ width: "150px" }} /> {/* US Class */}
            <col style={{ width: "150px" }} /> {/* GDPR Class */}
            <col style={{ width: "225px" }} /> {/* Documentation Status */}
            <col style={{ width: "50px" }} /> {/* Arrow */}
          </colgroup>
        ) : (
          <colgroup>
            <col style={{ width: "380px" }} /> {/* Vendor Name */}
            <col style={{ width: "160px" }} /> {/* Region */}
            <col style={{ width: "230px" }} /> {/* US Class */}
            <col style={{ width: "235px" }} /> {/* Documentation Status */}
            <col style={{ width: "50px" }} /> {/* Arrow */}
          </colgroup>
        )}
        <thead className="sticky top-0 z-10">
          <tr>
            {tableHeader(
              "Vendor Name",
              "pl-6 text-left",
              /* tooltipText = */ null,
              /* onSortClick = */ () => {
                if (sortOrder === "name_ascending") {
                  setSortOrder("name_descending");
                } else {
                  setSortOrder("name_ascending");
                }
              },
            )}
            {tableHeader("Region")}
            {tableHeader(
              "US Class",
              /* className = */ "",
              /* tooltipText = */ "Are they considered a service provider or a third party under U.S. privacy laws.",
              /* onSortClick = */ () => {
                if (sortOrder === "us_classification_ascending") {
                  setSortOrder("us_classification_descending");
                } else {
                  setSortOrder("us_classification_ascending");
                }
              },
            )}

            {org.featureGdpr &&
              tableHeader(
                "GDPR Class",
                /* className = */ "",
                /* tooltipText = */ "Are they considered a processor or controller under GDPR.",
                /* onSortClick = */ () => {
                  if (sortOrder === "gdpr_classification_ascending") {
                    setSortOrder("gdpr_classification_descending");
                  } else {
                    setSortOrder("gdpr_classification_ascending");
                  }
                },
              )}

            {tableHeader(
              "Docs Status",
              /* className = */ "",
              /* tooltipText = */ "Whether all required documentation for this vendor has been provided.",
              /* onSortClick = */ () => {
                if (sortOrder === "documentation_status_ascending") {
                  setSortOrder("documentation_status_descending");
                } else {
                  setSortOrder("documentation_status_ascending");
                }
              },
            )}

            {tableHeader(" ")}
          </tr>
        </thead>
        <tbody>
          <>
            {unverifiedVendorsSorted.length > 0 &&
              sectionHeaderRow(
                "Unverified",
                "bg-amber-100 text-amber-800 border-b border-solid border-neutral-200",
              )}
            {renderVendorRows(unverifiedVendorsSorted)}

            {recentlyContactedVendorsSorted.length > 0 &&
              sectionHeaderRow(
                "Recently Contacted",
                "bg-neutral-0 text-neutral-800 border-b border-solid border-neutral-200",
              )}
            {renderVendorRows(recentlyContactedVendorsSorted)}

            {classifiedVendorsSorted.length > 0 &&
              sectionHeaderRow(
                "Verified",
                "bg-neutral-0 text-neutral-800 border-b border-solid border-neutral-200",
              )}
            {renderVendorRows(classifiedVendorsSorted)}
          </>
        </tbody>
      </table>
    </div>
  );
}

const vendorComparisonFn = (
  a,
  b,
  sortOrder: VendorTableSortOrder,
  org: OrganizationDto,
  collectionContext: CollectionContext[],
) => {
  var fieldA = a.name,
    fieldB = b.name;

  if (sortOrder === "us_classification_ascending" || sortOrder === "us_classification_descending") {
    fieldA = getVendorClassificationCell(a, org, collectionContext).text;
    fieldB = getVendorClassificationCell(b, org, collectionContext).text;
  } else if (
    sortOrder === "gdpr_classification_ascending" ||
    sortOrder === "gdpr_classification_descending"
  ) {
    fieldA = getGdprClassificationCell(a).text;
    fieldB = getGdprClassificationCell(b).text;
  } else if (
    sortOrder === "documentation_status_ascending" ||
    sortOrder === "documentation_status_descending"
  ) {
    fieldA = getDocumentationStatusCell(org, a).text;
    fieldB = getDocumentationStatusCell(org, b).text;
  }

  let isAscending = sortOrder.endsWith("_ascending");

  let comparison = fieldA.localeCompare(fieldB);

  if (comparison != 0) {
    return isAscending ? comparison : -comparison;
  } else {
    return isAscending ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
  }
};

function VendorDocumentationTableRow({
  org,
  vendor,
  updateVendor,
  isSidebarOpen,
  openSidebar,
  closeSidebar,
  navigateNextVendorInSidebar,
  navigatePreviousVendorInSidebar,
  sellingAndSharingAnswers,
  allVendors,
  collectionContext,
  includeEmptyContext,
}: {
  org: OrganizationDto;
  vendor: OrganizationDataRecipientDto;
  updateVendor: (v: UpdateVendorArgs) => Promise<void>;
  isSidebarOpen: boolean;
  openSidebar: () => void;
  closeSidebar: () => void;
  navigateNextVendorInSidebar: () => void;
  navigatePreviousVendorInSidebar: () => void;
  sellingAndSharingAnswers: SurveyAnswers;
  allVendors: OrganizationDataRecipientDto[];
  collectionContext: CollectionContext[];
  includeEmptyContext: boolean;
}) {
  const tableCellStyles = clsx("border-b border-solid border-gray-200");

  // Calculate column widths based on GDPR status (must match the <colgroup> above)
  const vendorNameColWidth = org.featureGdpr ? "360px" : "380px";
  const regionColWidth = org.featureGdpr ? "120px" : "160px";
  const usClassColWidth = org.featureGdpr ? "150px" : "230px";
  const gdprClassColWidth = "150px";
  const docStatusColWidth = org.featureGdpr ? "225px" : "235px";
  const tableCell = (
    children: React.ReactNode,
    customStyles: { colWidth: string; px?: string; py?: string },
  ) => (
    <td
      className={tableCellStyles}
      style={{
        width: customStyles.colWidth,
        minWidth: customStyles.colWidth,
        maxWidth: customStyles.colWidth,
        paddingLeft: customStyles.px || "24px",
        paddingRight: customStyles.px || "24px",
        paddingTop: customStyles.py || "16px",
        paddingBottom: customStyles.py || "16px",
        lineHeight: "18px",
      }}
    >
      {children}
    </td>
  );

  const [isDisabledWhileLoading, setIsDisabledWhileLoading] = useState(false);
  const { disclosureMeta } = getVendorDocumentationDisclosureState(
    vendor,
    org,
    sellingAndSharingAnswers,
    allVendors,
  );
  const isThirdParty = isThirdPartyRecipient(vendor);
  const isEeaUkRegion =
    !isEmployeeOnlyCollectionContext(vendor.collectionContext ?? []) &&
    org.featureGdpr &&
    vendor.processingRegions.includes("EEA_UK");

  return (
    <>
      <tr
        key={vendor.id}
        className={clsx("border-t border-solid border-gray-200", "cursor-pointer", {
          "bg-neutral-0": isSidebarOpen,
        })}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          openSidebar();
        }}
      >
        {/* Vendor Name */}
        {tableCell(
          <div className="flex items-center gap-[12px]">
            <img src={vendor.logoUrl} alt={vendor.name} className="h-6 w-6 flex-shrink-0" />
            <span className="text-[#111827] font-medium text-[14px]">{vendor.name}</span>
            {isVendorRecentlyContacted(vendor) && vendor.serviceProviderInfoRequestSentDate && (
              <span className="text-emerald-700 font-medium text-[14px] whitespace-nowrap">
                Contacted{" "}
                {new Date(vendor.serviceProviderInfoRequestSentDate).toLocaleDateString(undefined, {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
              </span>
            )}
          </div>,
          { colWidth: vendorNameColWidth },
        )}

        {/* Region */}
        {tableCell(
          <div className="flex justify-center items-center gap-[3px]">
            {vendor.processingRegions
              .filter((r) => {
                // Even if the vendor has a non-USA region, need to also check region at the org-level
                if (r == "EEA_UK") {
                  return org.featureGdpr;
                }
                if (r == "CANADA") {
                  return org.featurePipeda;
                }
                return true;
              })
              .map((region: ProcessingRegion) => {
                return ProcessingRegionToFlagIcon[region];
              })}
          </div>,
          { colWidth: regionColWidth },
        )}

        {/* US Classification */}
        {tableCell(
          <div className="grid place-items-center">
            <BadgedTextPill
              cellPillDisplay={getVendorClassificationCell(vendor, org, collectionContext)}
            />
          </div>,
          { colWidth: usClassColWidth },
        )}

        {/* GDPR Classification */}
        {org.featureGdpr &&
          tableCell(
            <div className="grid place-items-center">
              <BadgedTextPill cellPillDisplay={getGdprClassificationCell(vendor)} />
            </div>,
            { colWidth: gdprClassColWidth },
          )}

        {/* Documentation Status */}
        {tableCell(
          <div className="grid place-items-center">
            <BadgedTextPill cellPillDisplay={getDocumentationStatusCell(org, vendor)} />
          </div>,
          { colWidth: docStatusColWidth },
        )}

        <td className={clsx(tableCellStyles, "w-[50px] max-w-[50px] min-w-[50px]")}>
          <ArrowRightCircleIcon
            className="w-[20px] h-[20px] text-primary-600 cursor-pointer"
            onClick={openSidebar}
          />
        </td>
      </tr>

      <Slideout
        open={isSidebarOpen}
        setOpen={(newIsOpen: boolean) => {
          if (newIsOpen) {
            openSidebar();
          } else {
            closeSidebar();
          }
        }}
        slideFrom="right"
        maxWidth="max-w-2xl"
      >
        <>
          {isDisabledWhileLoading && (
            <div className="absolute top-0 left-0 w-full h-full bg-white bg-opacity-50 z-10">
              <div className="flex items-center justify-center w-full h-full">
                <CircularProgress size="6em" />
              </div>
            </div>
          )}

          <div className="h-full flex" style={{ flexDirection: "column" }}>
            {/* Sidebar header (vendor name + logo + URL) */}
            <div
              className={clsx(
                "h-[150px] flex-shrink-0",
                "bg-neutral-0 p-[24px] border border-b-1 border-gray-200",
              )}
            >
              <div className="flex justify-between items-center">
                <h3 className="text-gray-900 text-[18px] font-bold">Vendor Documentation</h3>
                <div className="cursor-pointer" onClick={closeSidebar}>
                  <XMarkIcon className="w-6 h-6 text-[#9CA3AF]" />
                </div>
              </div>

              <div className="mt-1">
                <div className="flex items-center">
                  <img src={vendor.logoUrl} alt={vendor.name} className="h-5 w-5" />
                  <span className="text-gray-900 font-medium text-[18px] ml-[12px] mr-[12px]">
                    {vendor.name}
                  </span>
                </div>
                {vendor.url?.length > 0 && (
                  <div className="mt-1 text-[14px] font-normal text-gray-500">{vendor.url}</div>
                )}
              </div>
            </div>

            {/* Sidebar editing UI */}
            <div className="flex-1 overflow-y-auto">
              <Form
                onSubmit={() => {}}
                render={() => {
                  return (
                    <div className="divide-y divide-gray-200">
                      {/* Vendor Contact Email */}
                      <SettingRow label="Vendor Contact Email for Classification Queries">
                        <VendorDocumentationContactEmailField
                          vendor={vendor}
                          updateVendor={updateVendor}
                          disabled={isDisabledWhileLoading}
                          showLabel={false}
                        />
                        {!isVendorDocumentationClassified(
                          org,
                          vendor,
                          collectionContext,
                          includeEmptyContext,
                        ) && (
                          <div className="mt-[12px]">
                            <VendorDocumentationEmailVendorSection
                              org={org}
                              vendor={vendor}
                              collectionContext={collectionContext}
                              updateVendor={updateVendor}
                            />
                          </div>
                        )}
                      </SettingRow>

                      {/* Regions */}
                      <SettingRow
                        label="Regions"
                        helpText={`From which region(s) is customer data sent to ${vendor.name}?`}
                      >
                        <VendorDocumentationRegionsField
                          org={org}
                          vendor={vendor}
                          updateVendor={updateVendor}
                        />
                      </SettingRow>

                      {/* CA Contract Requirement */}
                      {isCaContractFieldRequiredForVendor(vendor) && (
                        <SettingRow
                          label="California Contract Requirement"
                          tooltip={
                            <ToolTip
                              text={
                                "Special data protections required by California law to be included in your contracts with vendors."
                              }
                              width="210px"
                            >
                              <InformationCircleIcon className="w-[16px] h-[16px] text-neutral-500" />
                            </ToolTip>
                          }
                          badge={
                            !isVendorCaContractDocumentationComplete(vendor) && (
                              <BadgedTextPill
                                cellPillDisplay={{ color: "yellow", text: "Unverified" }}
                              />
                            )
                          }
                          helpText={
                            <>
                              Upload or link to a document below, otherwise contact this vendor for
                              documentation. Click{" "}
                              <a
                                href="https://knowledge.truevault.com/article/353-sample-california-contract-template"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary-600 hover:text-primary-700 underline"
                              >
                                here
                              </a>{" "}
                              to access a contract template you can use to satisfy this requirement.
                            </>
                          }
                        >
                          <VendorDocumentationCaContractQuestion
                            org={org}
                            vendor={vendor}
                            updateVendor={updateVendor}
                            setIsDisabledWhileLoading={setIsDisabledWhileLoading}
                          />
                        </SettingRow>
                      )}

                      {/* Browser Opt-Out / GPP Signal */}
                      {doesGppSignalApplyForVendor(vendor) && (
                        <SettingRow
                          label="Browser Opt-Out"
                          tooltip={
                            <ToolTip
                              text={
                                "Global Privacy Protocol (GPP) signals are the industry standard for communicating advertising opt-outs and consent online. Upload or link to a document below demonstrating the vendor is an IAB GPP signatory."
                              }
                              width="210px"
                            >
                              <InformationCircleIcon className="w-[16px] h-[16px] text-neutral-500" />
                            </ToolTip>
                          }
                          badge={
                            !isVendorGppSignalDocumentationComplete(vendor) && (
                              <BadgedTextPill
                                cellPillDisplay={{ color: "yellow", text: "Unverified" }}
                              />
                            )
                          }
                          helpText={gppSignalHelpText(vendor)}
                        >
                          <VendorDocumentationBrowserOptOutQuestion
                            org={org}
                            vendor={vendor}
                            updateVendor={updateVendor}
                            setIsDisabledWhileLoading={setIsDisabledWhileLoading}
                          />
                        </SettingRow>
                      )}

                      {/* Platform Requests */}
                      {vendor.isPlatform !== true && (
                        <SettingRow
                          label="Platform Requests"
                          tooltip={
                            <ToolTip
                              text={
                                "Some platforms like Shopify automatically forward updates to customer lists (e.g., the deletion of a customer or a data sale opt-out) to installed apps. If an app respects these signals, it will be grouped under the platform in consent management workflows. For example, if a vendor respects Shopify's customer events, consumers only need to submit one request to Shopify rather than separate requests to each app."
                              }
                              width="280px"
                            >
                              <InformationCircleIcon className="w-[16px] h-[16px] text-neutral-500" />
                            </ToolTip>
                          }
                          badge={
                            vendor.vendorRespectsPlatformRequests && <TruevaultVerifiedBadge />
                          }
                          helpText={
                            vendor.vendorRespectsPlatformRequests
                              ? "This vendor has been verified by TrueVault to respect platform-level opt-out signals."
                              : "Indicate whether this vendor respects platform-level opt-out signals and deletion requests from platforms like Shopify."
                          }
                        >
                          <VendorDocumentationPlatformRequestsQuestion
                            vendor={vendor}
                            updateVendor={updateVendor}
                            setIsDisabledWhileLoading={setIsDisabledWhileLoading}
                          />
                        </SettingRow>
                      )}

                      {/* US Classification */}
                      {isUsClassificationRequiredForVendor(vendor, org, collectionContext) && (
                        <SettingRow
                          label={
                            isThirdParty ? "US Classification: Third Party" : "US Classification"
                          }
                          tooltip={
                            <ToolTip
                              text={`Upload or link to a document below, otherwise contact this vendor for documentation. Or, click the "Email Vendor" button above to send a templated email asking for documentation.`}
                              width="210px"
                            >
                              <InformationCircleIcon className="w-[16px] h-[16px] text-neutral-500" />
                            </ToolTip>
                          }
                          badge={
                            !doesVendorHaveUsClassification(vendor) && (
                              <BadgedTextPill
                                cellPillDisplay={{ color: "yellow", text: "Unverified" }}
                              />
                            )
                          }
                          helpText={usClassificationHelpText(vendor, disclosureMeta)}
                        >
                          <VendorDocumentationUsClassificationQuestion
                            org={org}
                            vendor={vendor}
                            updateVendor={updateVendor}
                            setIsDisabledWhileLoading={setIsDisabledWhileLoading}
                            sellingAndSharingAnswers={sellingAndSharingAnswers}
                            allVendors={allVendors}
                          />
                        </SettingRow>
                      )}

                      {/* GDPR Classification */}
                      {isEeaUkRegion && disclosureMeta.showGdprProcessorClassification && (
                        <SettingRow
                          label="GDPR Classification"
                          tooltip={
                            <ToolTip
                              text={`Upload or link to a document below, otherwise contact this vendor for documentation. Or, click the "Email Vendor" button above to send a templated email asking for documentation.`}
                              width="210px"
                            >
                              <InformationCircleIcon className="w-[16px] h-[16px] text-neutral-500" />
                            </ToolTip>
                          }
                          badge={
                            !isVendorGdprClassificationComplete(vendor) && (
                              <BadgedTextPill
                                cellPillDisplay={{ color: "yellow", text: "Unverified" }}
                              />
                            )
                          }
                          helpText={gdprClassificationHelpText(vendor)}
                        >
                          <VendorDocumentationGdprClassificationQuestion
                            org={org}
                            vendor={vendor}
                            updateVendor={updateVendor}
                            setIsDisabledWhileLoading={setIsDisabledWhileLoading}
                            sellingAndSharingAnswers={sellingAndSharingAnswers}
                            allVendors={allVendors}
                          />
                        </SettingRow>
                      )}

                      {/* International Data Transfers */}
                      {isEeaUkRegion && doesGdprInternationalDataTransferApplyForVendor(vendor) && (
                        <SettingRow
                          label="International Data Transfers"
                          badge={
                            !isGdprInternationalDataTransferComplete(vendor) && (
                              <BadgedTextPill
                                cellPillDisplay={{ color: "yellow", text: "Unverified" }}
                              />
                            )
                          }
                          helpText={
                            <>
                              Data transfers to {vendor.name} are considered "safe" under GDPR only
                              if {vendor.name} participates in the EU-US Data Privacy Framework
                              (DPF), if it incorporates Standard Contractual Clauses (SCCs) into its
                              agreements with your business, or if consumer's personal data is
                              transferred solely to fulfill a contract with the consumer. If you are
                              relying on {vendor.name}'s DPF participation or its SCCS for the
                              transfer, add a link to its DPF profile page or its online SCCS, or
                              alternatively you can upload an agreement which contains the SCCs.{" "}
                              <a
                                href="https://knowledge.truevault.com/article/202-standard-contractual-clauses"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary-600 underline"
                              >
                                Read more about SCCs and how to look for them
                              </a>
                              .
                            </>
                          }
                        >
                          <VendorDocumentationInternationalDataTransfersQuestion
                            org={org}
                            vendor={vendor}
                            updateVendor={updateVendor}
                            setIsDisabledWhileLoading={setIsDisabledWhileLoading}
                            sellingAndSharingAnswers={sellingAndSharingAnswers}
                            allVendors={allVendors}
                          />
                        </SettingRow>
                      )}
                    </div>
                  );
                }}
              />
            </div>

            {/* Sidebar footer */}
            <div
              className={clsx(
                "h-[62px] flex-shrink-0",
                "p-[16px] border border-t-1 border-gray-200",
                "flex items-center justify-between",
              )}
            >
              <Button
                color="tertiary"
                size="s"
                customSizeClassname="h-[30px] w-auto"
                onClick={navigatePreviousVendorInSidebar}
              >
                <div className="flex items-center">
                  <ArrowLeftIcon className="w-[16px] h-[16px] text-neutral-500" />
                  <span className="text-[12px] font-medium text-neutral-800 ml-[8px]">
                    Previous Vendor
                  </span>
                </div>
              </Button>
              <Button
                color="tertiary"
                size="s"
                customSizeClassname="h-[30px] w-auto"
                onClick={navigateNextVendorInSidebar}
              >
                <div className="flex items-center">
                  <ArrowRightIcon className="w-[16px] h-[16px] text-neutral-500" />
                  <span className="text-[12px] font-medium text-neutral-800 ml-[8px]">
                    Next Vendor
                  </span>
                </div>
              </Button>
            </div>
          </div>
        </>
      </Slideout>
    </>
  );
}

interface VendorDocumentationQuestionProps {
  org: OrganizationDto;
  vendor: OrganizationDataRecipientDto;
  updateVendor: (v: UpdateVendorArgs) => Promise<void>;
  setIsDisabledWhileLoading: (value: boolean) => void;
}

interface VendorDocumentationClassificationQuestionProps extends VendorDocumentationQuestionProps {
  sellingAndSharingAnswers: SurveyAnswers;
  allVendors: OrganizationDataRecipientDto[];
}

export function getVendorDocumentationDisclosureState(
  vendor: OrganizationDataRecipientDto,
  org: OrganizationDto,
  sellingAndSharingAnswers: SurveyAnswers,
  allVendors: OrganizationDataRecipientDto[],
) {
  const disclosureState = initialStateFrom(vendor, sellingAndSharingAnswers, org);
  const disclosureMeta = calculateDisclosureMeta(
    vendor,
    disclosureState,
    /* custom = */ false,
    sellingAndSharingAnswers,
    allVendors,
    vendor.category,
  );

  return { disclosureState, disclosureMeta };
}

export function VendorDocumentationCaContractQuestion({
  org,
  vendor,
  updateVendor,
  setIsDisabledWhileLoading,
}: VendorDocumentationQuestionProps) {
  return (
    <>
      <VendorRadioSection
        recipient={vendor}
        options={caContractOptionsForVendor()}
        value="ca-contract-requirement"
        hideDocumentationInput={vendor.cpraContractLanguageUrl?.length > 0}
        url={vendor.caContractRequirementUrl ?? vendor.cpraContractLanguageUrl ?? ""}
        fileName={
          vendor.caContractRequirementFileName === "DELETE_"
            ? null
            : vendor.caContractRequirementFileName || ""
        }
        fileKey={
          vendor.caContractRequirementFileName === "DELETE_"
            ? null
            : vendor.caContractRequirementFileName || ""
        }
        onSelectionChange={async () => {}}
        onUrlChange={async (newUrl: string) => {
          setIsDisabledWhileLoading(true);
          await updateVendor({
            vendorId: vendor.vendorId,
            vendorSettings: { ...vendor, caContractRequirementUrl: newUrl },
          });
          setIsDisabledWhileLoading(false);
        }}
        onFileUpload={async (fileName, fileData) => {
          setIsDisabledWhileLoading(true);
          await saveCaContractRequirementDocumentation(
            org,
            vendor,
            fileName,
            fileData,
            updateVendor,
          );
          setIsDisabledWhileLoading(false);
        }}
        onFileDelete={async () => {
          setIsDisabledWhileLoading(true);
          await saveCaContractRequirementDocumentation(org, vendor, "DELETE_", null, updateVendor);
          setIsDisabledWhileLoading(false);
        }}
        hideSelectionRadioIfOnlyOneOption
        useAquiloDesign
      />
      {vendor.cpraContractLanguageUrl?.length > 0 && (
        <div className="flex items-center gap-[6px]">
          <TruevaultVerifiedBadge />
          <div className="flex items-center gap-[4px]">
            <a
              href={vendor.cpraContractLanguageUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline text-[14px] font-medium"
            >
              View Documentation
            </a>
            <ArrowTopRightOnSquareIcon className="w-[16px] h-[16px] text-blue-600" />
          </div>
        </div>
      )}
    </>
  );
}

export function VendorDocumentationBrowserOptOutQuestion({
  org,
  vendor,
  updateVendor,
  setIsDisabledWhileLoading,
}: VendorDocumentationQuestionProps) {
  if (vendor.gppSignalVerifiedStatus && vendor.gppSignalVerifiedDocumentationUrl?.length > 0) {
    return (
      <div className="flex items-center gap-[6px]">
        <TruevaultVerifiedBadge />
        <div className="flex items-center gap-[4px]">
          <a
            href={vendor.gppSignalVerifiedDocumentationUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline text-[14px] font-medium"
          >
            View Documentation
          </a>
          <ArrowTopRightOnSquareIcon className="w-[16px] h-[16px] text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <VendorRadioSection
      recipient={vendor}
      options={[
        {
          key: "gpp-signal-respected",
          label: "Respects GPP Signal",
          type: "file",
          value: "GPP_SIGNAL_RESPECTED",
        },
        {
          key: "gpp-alternate-opt-out-mechanism",
          label: "Alternate opt-out mechanism",
          type: "normal",
          value: "GPP_ALTERNATE_OPT_OUT_MECHANISM",
        },
        {
          key: "gpp-signal-not-respected-or-unknown",
          label: "Unknown/does not respect GPP",
          type: "normal",
          value: "GPP_SIGNAL_NOT_RESPECTED_OR_UNKNOWN",
        },
      ]}
      value={vendor.gppSignalStatus}
      url={vendor.gppSignalDocumentationUrl ?? ""}
      fileName={
        vendor.gppSignalDocumentationFileName === "DELETE_"
          ? null
          : vendor.gppSignalDocumentationFileName || ""
      }
      fileKey={
        vendor.gppSignalDocumentationFileName === "DELETE_"
          ? null
          : vendor.gppSignalDocumentationFileKey || ""
      }
      onSelectionChange={async (newValue: GppSignalStatus) => {
        setIsDisabledWhileLoading(true);
        await updateVendor({
          vendorId: vendor.vendorId,
          vendorSettings: { ...vendor, gppSignalStatus: newValue },
        });
        setIsDisabledWhileLoading(false);
      }}
      onUrlChange={async (newUrl: string) => {
        setIsDisabledWhileLoading(true);
        await updateVendor({
          vendorId: vendor.vendorId,
          vendorSettings: { ...vendor, gppSignalDocumentationUrl: newUrl },
        });
        setIsDisabledWhileLoading(false);
      }}
      onFileUpload={async (fileName, fileData) => {
        setIsDisabledWhileLoading(true);
        await saveGppSignalDocumentation(org, vendor, fileName, fileData, updateVendor);
        setIsDisabledWhileLoading(false);
      }}
      onFileDelete={async () => {
        setIsDisabledWhileLoading(true);
        await saveGppSignalDocumentation(org, vendor, "DELETE_", null, updateVendor);
        setIsDisabledWhileLoading(false);
      }}
      useAquiloDesign
    />
  );
}

export function VendorDocumentationPlatformRequestsQuestion({
  vendor,
  updateVendor,
  setIsDisabledWhileLoading,
}: Omit<VendorDocumentationQuestionProps, "org">) {
  if (vendor.vendorRespectsPlatformRequests) {
    return (
      <div className="text-neutral-800 text-[14px] font-medium bg-neutral-100 rounded-[6px] px-[12px] py-[8px]">
        Respects Platform Requests
      </div>
    );
  }

  return (
    <VendorRadioSection
      recipient={vendor}
      options={[
        {
          key: "platform-requests-yes",
          label: "Respects platform requests",
          type: "normal",
          value: "true",
        },
        {
          key: "platform-requests-no",
          label: "Does not respect platform requests (Recommended)",
          type: "normal",
          value: "false",
        },
      ]}
      value={
        vendor.respectsPlatformRequests === true
          ? "true"
          : vendor.respectsPlatformRequests === false
          ? "false"
          : null
      }
      url=""
      fileName=""
      fileKey=""
      onSelectionChange={async (newValue: string) => {
        setIsDisabledWhileLoading(true);
        await updateVendor({
          vendorId: vendor.vendorId,
          vendorSettings: { ...vendor, respectsPlatformRequests: newValue === "true" },
        });
        setIsDisabledWhileLoading(false);
      }}
      onUrlChange={async () => {}}
      onFileUpload={async () => {}}
      onFileDelete={async () => {}}
      hideDocumentationInput
      useAquiloDesign
    />
  );
}

export function VendorDocumentationUsClassificationQuestion({
  org,
  vendor,
  updateVendor,
  setIsDisabledWhileLoading,
  sellingAndSharingAnswers,
  allVendors,
}: VendorDocumentationClassificationQuestionProps) {
  const { disclosureState, disclosureMeta } = getVendorDocumentationDisclosureState(
    vendor,
    org,
    sellingAndSharingAnswers,
    allVendors,
  );

  return (
    <>
      <VendorRadioSection
        recipient={vendor}
        options={usClassificationOptionsForVendor(vendor, disclosureMeta)}
        value={disclosureState.classification}
        url={disclosureState.publicTosUrl ?? ""}
        fileName={vendor.tosFileName === "DELETE_" ? null : vendor.tosFileName || ""}
        fileKey={vendor.tosFileName === "DELETE_" ? null : vendor.tosFileName || ""}
        onSelectionChange={async (classification: ClassificationType) => {
          setIsDisabledWhileLoading(true);
          await updateVendor({
            vendorId: vendor.vendorId,
            vendorSettings: {
              ...vendor,
              classificationId: getClassificationIdForClassificationType(
                classification,
                vendor.classificationOptions,
              ),
            },
          });
          setIsDisabledWhileLoading(false);
        }}
        onUrlChange={async (publicTosUrl) => {
          setIsDisabledWhileLoading(true);
          const updatedVendor = { ...vendor, publicTosUrl };
          if (publicTosUrl) {
            updatedVendor.serviceProviderInfoRequestSentDate = "1970-01-01T00:00:00Z";
          }
          await updateVendor({ vendorId: vendor.vendorId, vendorSettings: updatedVendor });
          setIsDisabledWhileLoading(false);
        }}
        hideDocumentationInput={!disclosureMeta.showServiceProviderDocumentationInput}
        onFileUpload={async (fileName, fileData) => {
          setIsDisabledWhileLoading(true);
          await saveServiceProviderDocumentation(
            org,
            vendor,
            fileName,
            fileData,
            disclosureState,
            updateVendor,
          );
          setIsDisabledWhileLoading(false);
        }}
        onFileDelete={async () => {
          setIsDisabledWhileLoading(true);
          await saveServiceProviderDocumentation(
            org,
            vendor,
            "DELETE_",
            null,
            disclosureState,
            updateVendor,
          );
          setIsDisabledWhileLoading(false);
        }}
        forceShowOptional={isThirdPartyDocumentationOptional(disclosureState)}
        useAquiloDesign
      />
      {disclosureMeta.showServiceProviderDocumentationInput && (
        <div className="mt-[12px]">
          <VendorBooleanQuestion
            question="Contract stored internally"
            questionKey="contract-stored-internally"
            condensed={false}
            helpText="Select `Yes` if the contract documentation is stored internally"
            value={vendor.contractStoredInternally}
            onChange={async (newValue) => {
              setIsDisabledWhileLoading(true);
              await updateVendor({
                vendorId: vendor.vendorId,
                vendorSettings: { ...vendor, contractStoredInternally: newValue },
              });
              setIsDisabledWhileLoading(false);
            }}
            useAquiloDesign
          />
        </div>
      )}
    </>
  );
}

export function VendorDocumentationGdprClassificationQuestion({
  org,
  vendor,
  updateVendor,
  setIsDisabledWhileLoading,
  sellingAndSharingAnswers,
  allVendors,
}: VendorDocumentationClassificationQuestionProps) {
  const { disclosureState, disclosureMeta } = getVendorDocumentationDisclosureState(
    vendor,
    org,
    sellingAndSharingAnswers,
    allVendors,
  );
  const isGdprProcessor = vendor.gdprProcessorSetting === "Processor";
  const documentationUrl = isGdprProcessor
    ? vendor.gdprProcessorGuaranteeUrl
    : vendor.gdprControllerGuaranteeUrl;
  const documentationFileName = isGdprProcessor
    ? vendor.gdprProcessorGuaranteeFileName
    : vendor.gdprControllerGuaranteeFileName;
  const documentationFileKey = isGdprProcessor
    ? vendor.gdprProcessorGuaranteeFileKey
    : vendor.gdprControllerGuaranteeFileKey;
  const options = gdprClassificationOptionsForVendor();
  const optionsByKey = _.keyBy(options, "key");

  if (disclosureState.processorClassification === "Unknown") {
    optionsByKey.unknown.disabled = true;
    optionsByKey.unknown.visible = true;
  } else {
    optionsByKey.unknown.visible = false;
  }

  return (
    <>
      <VendorRadioSection
        recipient={vendor}
        options={options}
        value={vendor.gdprProcessorSetting}
        hideDocumentationInput={
          (vendor.gdprProcessorRecommendation === "Processor" ||
            vendor.gdprProcessorRecommendation === "Controller") &&
          vendor.gdprProcessorLanguageUrl?.length > 0
        }
        url={documentationUrl || vendor.gdprProcessorLanguageUrl}
        fileName={documentationFileName === "DELETE_" ? null : documentationFileName || ""}
        fileKey={documentationFileName === "DELETE_" ? null : documentationFileKey || ""}
        onSelectionChange={async (setting: GDPRProcessorRecommendation) => {
          setIsDisabledWhileLoading(true);
          await updateVendor({
            vendorId: vendor.vendorId,
            vendorSettings: { ...vendor, gdprProcessorSetting: setting },
          });
          setIsDisabledWhileLoading(false);
        }}
        onUrlChange={async (newUrl: string) => {
          setIsDisabledWhileLoading(true);
          const updatedVendor = { ...vendor };
          if (vendor.gdprProcessorSetting === "Processor") {
            updatedVendor.gdprProcessorGuaranteeUrl = newUrl;
          } else if (vendor.gdprProcessorSetting === "Controller") {
            updatedVendor.gdprControllerGuaranteeUrl = newUrl;
          }
          if (newUrl) {
            updatedVendor.serviceProviderInfoRequestSentDate = "1970-01-01T00:00:00Z";
          }
          await updateVendor({ vendorId: vendor.vendorId, vendorSettings: updatedVendor });
          setIsDisabledWhileLoading(false);
        }}
        onFileUpload={async (fileName, fileData) => {
          setIsDisabledWhileLoading(true);
          await saveGdprDocumentation(
            org,
            vendor,
            fileName,
            fileData,
            disclosureState,
            updateVendor,
          );
          setIsDisabledWhileLoading(false);
        }}
        onFileDelete={async () => {
          setIsDisabledWhileLoading(true);
          await saveGdprDocumentation(org, vendor, "DELETE_", null, disclosureState, updateVendor);
          setIsDisabledWhileLoading(false);
        }}
        disabled={disclosureMeta.lockGdprProcessorClassification}
        forceShowOptional={isGDPRControllerDocumentationOptional(disclosureState)}
        useAquiloDesign
      />
      {disclosureMeta.showServiceProviderDocumentationInput && (
        <div className="mt-[12px]">
          <VendorBooleanQuestion
            question="GDPR contract stored internally"
            questionKey="gdpr-contract-stored-internally"
            condensed={false}
            helpText="Select `Yes` if the contract documentation is stored internally"
            value={vendor.gdprContractStoredInternally}
            onChange={async (newValue) => {
              setIsDisabledWhileLoading(true);
              await updateVendor({
                vendorId: vendor.vendorId,
                vendorSettings: { ...vendor, gdprContractStoredInternally: newValue },
              });
              setIsDisabledWhileLoading(false);
            }}
            useAquiloDesign
          />
        </div>
      )}
    </>
  );
}

export function VendorDocumentationInternationalDataTransfersQuestion({
  org,
  vendor,
  updateVendor,
  setIsDisabledWhileLoading,
  sellingAndSharingAnswers,
  allVendors,
}: VendorDocumentationClassificationQuestionProps) {
  const [isSafeCountriesDialogOpen, setIsSafeCountriesDialogOpen] = useState(false);
  const { disclosureState } = getVendorDocumentationDisclosureState(
    vendor,
    org,
    sellingAndSharingAnswers,
    allVendors,
  );
  const value =
    vendor.gdprSccVendorReviewStatus === null && vendor.gdprHasSccRecommendation
      ? "DPF_LIST"
      : vendor.gdprSccVendorReviewStatus;

  return (
    <>
      <VendorRadioSection
        recipient={vendor}
        options={internationalDataTransferOptionsForVendor(vendor, org, () => {
          setIsSafeCountriesDialogOpen(true);
        })}
        value={value}
        url={vendor.gdprSccUrl ?? ""}
        fileName={vendor.gdprSccFileName === "DELETE_" ? null : vendor.gdprSccFileName || ""}
        fileKey={vendor.gdprSccFileName === "DELETE_" ? null : vendor.gdprSccFileKey || ""}
        onSelectionChange={async (newValue: GdprSccVendorReviewStatus) => {
          await updateVendor({
            vendorId: vendor.vendorId,
            vendorSettings: { ...vendor, gdprSccVendorReviewStatus: newValue },
          });
        }}
        onFileUpload={async (fileName, fileData) => {
          setIsDisabledWhileLoading(true);
          await saveSccDocumentation(
            org,
            vendor,
            fileName,
            fileData,
            disclosureState,
            updateVendor,
          );
          setIsDisabledWhileLoading(false);
        }}
        onFileDelete={async () => {
          setIsDisabledWhileLoading(true);
          await saveSccDocumentation(org, vendor, "DELETE_", null, disclosureState, updateVendor);
          setIsDisabledWhileLoading(false);
        }}
        onUrlChange={async (newUrl) => {
          const updatedVendor = { ...vendor, gdprSccUrl: newUrl };
          if (newUrl) {
            updatedVendor.serviceProviderInfoRequestSentDate = "1970-01-01T00:00:00Z";
          }
          await updateVendor({ vendorId: vendor.vendorId, vendorSettings: updatedVendor });
        }}
        hideDocumentationInput={false}
        forceShowOptional={isThirdPartyDocumentationOptional(disclosureState)}
        useAquiloDesign
      />
      <SafeCountriesDialog
        open={isSafeCountriesDialogOpen}
        onClose={() => setIsSafeCountriesDialogOpen(false)}
      />
    </>
  );
}

// A single setting in the vendor slideout, rendered in the wider two-column "drawer" layout:
// the label (+ optional tooltip/badge) sits in the left column, and the help text + control
// occupy the right two-thirds. Centralizing the typography here keeps every setting consistent.
function SettingRow({
  label,
  tooltip,
  badge,
  helpText,
  children,
}: {
  label: React.ReactNode;
  tooltip?: React.ReactNode;
  badge?: React.ReactNode;
  helpText?: React.ReactNode;
  children?: React.ReactNode;
}) {
  // Render the tooltip icon inline, immediately after the last word of the label, and keep that
  // word + icon together (whitespace-nowrap) so they wrap to the next line as a pair instead of
  // the icon spilling onto a line by itself.
  const iconSpan = tooltip ? (
    <span className="ml-[6px] inline-flex items-center align-middle">{tooltip}</span>
  ) : null;

  let labelContent: React.ReactNode;
  if (tooltip && typeof label === "string") {
    const words = label.split(" ");
    const lastWord = words.pop();
    const lead = words.join(" ");
    labelContent = (
      <>
        {lead && `${lead} `}
        <span className="whitespace-nowrap">
          {lastWord}
          {iconSpan}
        </span>
      </>
    );
  } else {
    labelContent = (
      <>
        {label}
        {iconSpan}
      </>
    );
  }

  return (
    <div className="px-[24px] py-[20px] sm:grid sm:grid-cols-3 sm:gap-[24px]">
      <div>
        <div className="text-[14px] text-neutral-900 font-medium leading-[20px]">
          {labelContent}
        </div>
        {badge && <div className="mt-[8px] flex">{badge}</div>}
      </div>
      <div className="sm:col-span-2 mt-[8px] sm:mt-0">
        {helpText && (
          <div className="text-neutral-800 text-[14px] font-normal leading-[18px] mb-[12px]">
            {helpText}
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

export function VendorDocumentationContactEmailField({
  vendor,
  updateVendor,
  disabled,
  fieldName = "vendor-classification-contact-email",
  showLabel = true,
}: {
  vendor: OrganizationDataRecipientDto;
  updateVendor: (v: UpdateVendorArgs) => Promise<void>;
  disabled: boolean;
  fieldName?: string;
  showLabel?: boolean;
}) {
  return (
    <>
      {showLabel && (
        <label className="block text-[14px] font-medium text-gray-700">
          Vendor Contact Email for Classification Queries
        </label>
      )}
      <TextInput
        field={fieldName}
        onBlur={async (e: React.FocusEvent<HTMLInputElement>) => {
          const newEmail = e.target.value.trim() || null;

          await updateVendor({
            vendorId: vendor.vendorId,
            vendorSettings: {
              ...vendor,
              email: newEmail,
            },
          });
        }}
        defaultValue={vendor.email || vendor.defaultEmail}
        maxLength={90}
        disabled={disabled}
      />
    </>
  );
}

export function VendorDocumentationEmailVendorSection({
  org,
  vendor,
  collectionContext = "CONSUMER",
  updateVendor,
  emailDialogZIndex,
}: {
  org: OrganizationDto;
  vendor: OrganizationDataRecipientDto;
  collectionContext?: CollectionContext | CollectionContext[];
  updateVendor: (v: UpdateVendorArgs) => Promise<void>;
  /** Use when the section is rendered inside a higher-layer modal. */
  emailDialogZIndex?: number;
}) {
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const vendorEmail = vendor.email || vendor.defaultEmail;
  const hasValidVendorEmail = Boolean(
    vendorEmail && vendorEmail.length > 0 && validator.isEmail(vendorEmail),
  );
  const canSendEmail = canSendPrivacyEmail(org);

  return (
    <>
      <EmailVendorButton
        onEmailVendorClick={() => setIsEmailDialogOpen(true)}
        emailSentDate={vendor.serviceProviderInfoRequestSentDate}
        disableSendEmailButton={!hasValidVendorEmail || !canSendEmail}
        disableSendEmailButtonTooltip={
          hasValidVendorEmail
            ? canSendEmail
              ? null
              : PRIVACY_EMAIL_SETUP_REQUIRED_TOOLTIP
            : "No contact email set"
        }
      />
      <EmailVendorDialog
        org={org}
        vendor={vendor}
        collectionContext={collectionContext}
        updateVendor={updateVendor}
        isOpen={isEmailDialogOpen}
        closeModal={() => setIsEmailDialogOpen(false)}
        zIndex={emailDialogZIndex}
      />
    </>
  );
}

export function VendorDocumentationRegionsField({
  org,
  vendor,
  updateVendor,
  namePrefix = "",
  regionCheckboxLayout = "vertical",
}: {
  org: OrganizationDto;
  vendor: OrganizationDataRecipientDto;
  updateVendor: (v: UpdateVendorArgs) => Promise<void>;
  namePrefix?: string;
  regionCheckboxLayout?: "vertical" | "horizontal";
}) {
  const isUsRegion = vendor.processingRegions.includes("UNITED_STATES");

  return (
    <>
      <div
        className={clsx(
          regionCheckboxLayout === "horizontal" && "flex flex-wrap items-center gap-x-[28px]",
        )}
      >
        {org.featureGdpr && (
          <VendorRegionCheckbox
            vendor={vendor}
            region="EEA_UK"
            updateVendor={updateVendor}
            namePrefix={namePrefix}
            labelMarginClassName={regionCheckboxLayout === "horizontal" ? "ml-[6px]" : undefined}
          />
        )}
        {org.featurePipeda && (
          <VendorRegionCheckbox
            vendor={vendor}
            region="CANADA"
            updateVendor={updateVendor}
            namePrefix={namePrefix}
            labelMarginClassName={regionCheckboxLayout === "horizontal" ? "ml-[6px]" : undefined}
          />
        )}
        <VendorRegionCheckbox
          vendor={vendor}
          region="UNITED_STATES"
          namePrefix={namePrefix}
          labelMarginClassName={regionCheckboxLayout === "horizontal" ? "ml-[6px]" : undefined}
          updateVendor={async (v: UpdateVendorArgs) => {
            const isUsRegionEnabled =
              v.vendorSettings.processingRegions.find((r) => r == "UNITED_STATES") !== undefined;
            if (!isUsRegionEnabled) {
              await Api.storage.deleteOrganizationVendorAgreement(
                org.id,
                vendor.vendorId,
                vendor.tosFileKey,
              );

              v.vendorSettings.serviceProviderInfoRequestSentDate = "1970-01-01T00:00:00Z";
            }

            await updateVendor(v);
          }}
        />
      </div>
      {isUsRegion && (
        <div className="ml-[30px] mt-[6px]">
          <SimpleCheckbox
            name={`${namePrefix}regulated-by-other-us-privacy-laws`}
            label={
              <div className="ml-[12px] text-neutral-700 text-[14px] font-normal leading-[16px]">
                This vendor is regulated by another privacy law (GLBA, FCRA, HIPAA, FERPA)
              </div>
            }
            initialChecked={vendor.regulatedByOtherUSPrivacyLaws}
            onChange={async (newChecked) => {
              await updateVendor({
                vendorId: vendor.vendorId,
                vendorSettings: {
                  ...vendor,
                  regulatedByOtherUSPrivacyLaws: newChecked,
                },
              });
            }}
          />
        </div>
      )}
    </>
  );
}

function EmailVendorButton({
  onEmailVendorClick,
  emailSentDate,
  disableSendEmailButton,
  disableSendEmailButtonTooltip,
}: {
  onEmailVendorClick?: () => void;
  emailSentDate: Iso8601Date | null;
  disableSendEmailButton?: boolean;
  disableSendEmailButtonTooltip?: string | null;
}) {
  let emailButton = (
    <Button color="primary" size="s" onClick={onEmailVendorClick} disabled={disableSendEmailButton}>
      <div className="flex items-center">
        <div>
          <EnvelopeIcon style={{ width: "16px", height: "16px", color: "white" }} />
        </div>
        <div className="text-[12px] font-medium text-white ml-[8px]">Email Vendor</div>
      </div>
    </Button>
  );

  if (
    disableSendEmailButton &&
    disableSendEmailButtonTooltip &&
    disableSendEmailButtonTooltip.length > 0
  ) {
    emailButton = <ToolTip text={disableSendEmailButtonTooltip}>{emailButton}</ToolTip>;
  }

  return (
    <div className="tw-boundary">
      <div className="mt-[6px]">{emailButton}</div>
      {emailSentDate && emailSentDate !== "1970-01-01T00:00:00Z" && (
        <div className="mt-[12px] bg-green-100 rounded-[6px] px-[12px] py-[7px] flex items-center">
          <div>
            <CheckCircleIcon className="w-[16px] h-[16px] text-green-400" />
          </div>
          <div className="ml-[12px] text-green-800 font-medium text-[12px]">
            You emailed this vendor on {Fmt.Date.fmtDate(emailSentDate)}
          </div>
        </div>
      )}
    </div>
  );
}

export function BadgedTextPill({
  cellPillDisplay,
}: {
  cellPillDisplay: VendorTableCellPillDisplay | VendorTableCellPillDisplay[];
}) {
  if (!cellPillDisplay) {
    return null;
  }

  if (Array.isArray(cellPillDisplay)) {
    if (cellPillDisplay.length === 1) {
      return <SingleBadgedTextPill cellPillDisplay={cellPillDisplay[0]} />;
    }

    return (
      <div className="flex flex-wrap gap-1 items-center justify-center">
        {cellPillDisplay.map((pill, idx) => (
          <SingleBadgedTextPill key={idx} cellPillDisplay={pill} />
        ))}
      </div>
    );
  }

  return <SingleBadgedTextPill cellPillDisplay={cellPillDisplay} />;
}

function SingleBadgedTextPill({
  cellPillDisplay,
}: {
  cellPillDisplay: VendorTableCellPillDisplay;
}) {
  if (!cellPillDisplay) {
    return null;
  }

  let textColor = {
    gray: "text-gray-800",
    yellow: "text-amber-800",
    green: "text-green-800",
    blue: "text-blue-800",
  }[cellPillDisplay.color];

  let bgColor = {
    gray: "bg-gray-100",
    yellow: "bg-amber-100",
    green: "bg-green-100",
    blue: "bg-blue-100",
  }[cellPillDisplay.color];

  if (cellPillDisplay.text === "-") {
    return <hr className="w-[51px] text-neutral-800 mx-auto" />;
  }

  if (cellPillDisplay.text === "GREEN_CHECKMARK_ICON") {
    return (
      <div className="flex items-center justify-center bg-green-100 rounded-[12px] w-[20px] h-[20px] p-[4px]">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="13"
          height="12"
          viewBox="0 0 13 12"
          fill="none"
        >
          <path
            d="M2.59985 6.5L4.59985 8.5L9.59985 3.5"
            stroke="#059669"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    );
  }

  if (cellPillDisplay.text === "GRAY_X_ICON") {
    return (
      <div className="flex items-center justify-center bg-neutral-100 rounded-[12px] w-[20px] h-[20px] p-[4px]">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="13"
          height="12"
          viewBox="0 0 13 12"
          fill="none"
        >
          <path
            d="M4.46025 3.16475C4.24058 2.94508 3.88442 2.94508 3.66475 3.16475C3.44508 3.38442 3.44508 3.74058 3.66475 3.96025L5.70451 6L3.66475 8.03975C3.44508 8.25942 3.44508 8.61558 3.66475 8.83525C3.88442 9.05492 4.24058 9.05492 4.46025 8.83525L6.5 6.79549L8.53975 8.83525C8.75942 9.05492 9.11558 9.05492 9.33525 8.83525C9.55492 8.61558 9.55492 8.25942 9.33525 8.03975L7.29549 6L9.33525 3.96025C9.55492 3.74058 9.55492 3.38442 9.33525 3.16475C9.11558 2.94508 8.75942 2.94508 8.53975 3.16475L6.5 5.20451L4.46025 3.16475Z"
            fill="#726D83"
          />
        </svg>
      </div>
    );
  }

  return <BadgedText text={cellPillDisplay.text} textColor={textColor} bgColor={bgColor} />;
}

function BadgedText({
  text,
  textColor,
  bgColor,
}: {
  text: string;
  textColor: string;
  bgColor: string;
}) {
  return (
    <div
      className={clsx(
        "px-[10px] py-[2px] max-w-[188px] max-h-[18px] rounded-full",
        "inline-flex justify-center items-center",
        "truncate font-medium text-[12px]",
        textColor,
        bgColor,
      )}
    >
      {text}
    </div>
  );
}

export function TruevaultVerifiedBadge() {
  return (
    <div className="bg-purple-100 h-[20px] pl-[8px] pr-[10px] py-[2px] flex items-center rounded-[10px]">
      <div>
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="6" cy="6" r="6" fill="#5331C6" />
          <path
            d="M6.00049 5.15234C6.28117 5.15251 6.5083 5.38041 6.5083 5.66113C6.5082 5.82669 6.42776 5.97168 6.30518 6.06445V6.47461C6.30503 6.64298 6.16791 6.7793 5.99951 6.7793C5.83133 6.77903 5.69497 6.64281 5.69482 6.47461V6.06445C5.57237 5.97167 5.4918 5.82659 5.4917 5.66113C5.4917 5.38031 5.71967 5.15234 6.00049 5.15234Z"
            fill="white"
          />
          <path
            d="M8.27096 6.03367C8.27096 7.2693 7.2693 8.27096 6.03367 8.27096C4.79805 8.27096 3.79639 7.2693 3.79639 6.03367C3.79639 4.79805 4.79805 3.79639 6.03367 3.79639C7.2693 3.79639 8.27096 4.79805 8.27096 6.03367Z"
            stroke="white"
            strokeWidth="0.745763"
          />
          <path
            d="M6.57622 2.2373C6.57622 1.95648 6.34857 1.72883 6.06775 1.72883C5.78692 1.72883 5.55927 1.95648 5.55927 2.2373H6.06775H6.57622ZM2.52554 7.49187C2.28234 7.63228 2.19901 7.94326 2.33943 8.18646C2.47984 8.42966 2.79081 8.51298 3.03401 8.37257L2.77978 7.93222L2.52554 7.49187ZM9.10147 8.37257C9.34467 8.51298 9.65565 8.42966 9.79606 8.18646C9.93647 7.94326 9.85314 7.63228 9.60994 7.49187L9.35571 7.93222L9.10147 8.37257ZM7.96623 7.13L7.71199 7.57035L9.10147 8.37257L9.35571 7.93222L9.60994 7.49187L8.22046 6.68965L7.96623 7.13ZM6.06775 3.83053H6.57622V2.2373H6.06775H5.55927V3.83053H6.06775ZM4.10182 7.16894L3.84758 6.72859L2.52554 7.49187L2.77978 7.93222L3.03401 8.37257L4.35606 7.60929L4.10182 7.16894Z"
            fill="white"
          />
        </svg>
      </div>
      <div className="font-medium text-primary-600 text-[12px] ml-[6px]">Verified</div>
    </div>
  );
}
